import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
  styled,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApp } from '../../hooks/useApp';
import { getRequestUser } from '../../services/users/UserService';

const Title = styled(DialogTitle)(() => ({
  backgroundColor: '#e1e8ee',
  color: '#3e627c!important',
  fontFamily: 'D-dinExp',
  fontWeight: 400,
  fontSize: '17.5px',
}));

const Actions = styled(DialogActions)(() => ({
  borderTop: '1px solid #dee2e6',
}));

interface Recipient {
  name: string;
  email: string;
  isAdmin?: boolean;
}

interface SendEmailDialogProps {
  open: boolean;
  onClose: () => void;
  onSend: (recipients: Recipient[]) => Promise<void>;
  selectedCount: number;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const SendEmailDialog = ({
  open,
  onClose,
  onSend,
  selectedCount,
}: SendEmailDialogProps) => {
  const { t } = useTranslation();
  const { authInfo, company, isAdmin } = useApp();
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [sending, setSending] = useState(false);
  const [errors, setErrors] = useState<Record<number, string>>({});

  useEffect(() => {
    if (open) {
      loadAdminEmails();
    }
  }, [open]);

  const loadAdminEmails = async () => {
    try {
      const companyId = isAdmin && company ? company : authInfo.companyId;
      const data: any = await getRequestUser(0, '', companyId);
      if (data?.content) {
        const adminRecipients: Recipient[] = data.content
          .filter((user: any) => user.status === 0 && user.userRole?.adminLogin === true)
          .map((user: any) => ({
            name: `${user.firstName || ''} ${user.lastName || ''}`.trim(),
            email: user.username || '',
            isAdmin: true,
          }));
        setRecipients(adminRecipients.length > 0 ? adminRecipients : [{ name: '', email: '', isAdmin: false }]);
      } else {
        setRecipients([{ name: '', email: '', isAdmin: false }]);
      }
    } catch {
      setRecipients([{ name: '', email: '', isAdmin: false }]);
    }
  };

  const handleAddRecipient = () => {
    setRecipients([...recipients, { name: '', email: '', isAdmin: false }]);
  };

  const handleRemoveRecipient = (index: number) => {
    const updated = recipients.filter((_, i) => i !== index);
    setRecipients(updated.length > 0 ? updated : [{ name: '', email: '', isAdmin: false }]);
    const newErrors = { ...errors };
    delete newErrors[index];
    setErrors(newErrors);
  };

  const handleChange = (index: number, field: 'name' | 'email', value: string) => {
    const updated = [...recipients];
    updated[index] = { ...updated[index], [field]: value };
    setRecipients(updated);
    if (field === 'email' && errors[index]) {
      const newErrors = { ...errors };
      delete newErrors[index];
      setErrors(newErrors);
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<number, string> = {};
    let valid = true;

    recipients.forEach((r, i) => {
      if (!r.email.trim()) {
        newErrors[i] = t('fieldRequired');
        valid = false;
      } else if (!EMAIL_REGEX.test(r.email.trim())) {
        newErrors[i] = t('invalidEmailFormat');
        valid = false;
      }
    });

    setErrors(newErrors);
    return valid;
  };

  const handleSend = async () => {
    if (!validate()) return;
    setSending(true);
    try {
      await onSend(recipients.filter(r => r.email.trim()));
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    if (!sending) {
      setRecipients([]);
      setErrors({});
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <Title sx={{ pt: 4, px: 4 }}>
        {t('sendReceipts')} ({selectedCount} {selectedCount === 1 ? t('record') : t('records')})
      </Title>
      <DialogContent sx={{ px: 4, pb: 0 }}>
        <Typography variant="body2" sx={{ mt: 2, mb: 2, color: 'text.secondary' }}>
          {t('emailRecipients')}
        </Typography>
        {recipients.map((recipient, index) => (
          <Grid container spacing={2} key={index} sx={{ mb: 1 }} alignItems="center">
            <Grid item xs={4}>
              <TextField
                fullWidth
                size="small"
                label={t('firstName')}
                value={recipient.name}
                onChange={(e) => handleChange(index, 'name', e.target.value)}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                label={t('email')}
                value={recipient.email}
                onChange={(e) => handleChange(index, 'email', e.target.value)}
                variant="outlined"
                error={!!errors[index]}
                helperText={errors[index]}
              />
            </Grid>
            <Grid item xs={2}>
              <IconButton
                onClick={() => handleRemoveRecipient(index)}
                color="error"
                size="small"
              >
                <DeleteIcon />
              </IconButton>
            </Grid>
          </Grid>
        ))}
        <Button
          startIcon={<AddIcon />}
          onClick={handleAddRecipient}
          size="small"
          sx={{ mt: 1, mb: 2 }}
        >
          {t('addEmail')}
        </Button>
      </DialogContent>
      <Actions sx={{ pb: 4, px: 4 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSend}
          disabled={sending}
          sx={{ mt: 2, mr: 2 }}
        >
          {sending ? <CircularProgress size={24} /> : t('sendReceipt')}
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleClose}
          disabled={sending}
          sx={{ mt: 2, mr: 2 }}
        >
          {t('cancel')}
        </Button>
      </Actions>
    </Dialog>
  );
};

export default SendEmailDialog;
